import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["state", "size"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useContext, useMemo, useState } from 'react';
import { getWindow } from 'ariakit-utils/dom';
import { useForkRef, useSafeLayoutEffect } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { P as PopoverContext } from '../__utils-3e6151ed.js';
import { jsx, jsxs } from 'react/jsx-runtime';
/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Modifications Copyright 2019 - present by Diego Haz.
 *
 * Extracted the SVG path only.
 */

var POPOVER_ARROW_PATH = "M23,27.8c1.1,1.2,3.4,2.2,5,2.2h2H0h2c1.7,0,3.9-1,5-2.2l6.6-7.2c0.7-0.8,2-0.8,2.7,0L23,27.8L23,27.8z";
var defaultSize = 30;
var halfDefaultSize = defaultSize / 2;
var rotateMap = {
  top: "rotate(180 " + halfDefaultSize + " " + halfDefaultSize + ")",
  right: "rotate(-90 " + halfDefaultSize + " " + halfDefaultSize + ")",
  bottom: "rotate(0 " + halfDefaultSize + " " + halfDefaultSize + ")",
  left: "rotate(90 " + halfDefaultSize + " " + halfDefaultSize + ")"
};

function useComputedStyle(element) {
  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      style = _useState2[0],
      setStyle = _useState2[1];

  useSafeLayoutEffect(function () {
    if (!element) return;
    var computedStyle = getWindow(element).getComputedStyle(element);
    setStyle(computedStyle);
  }, [element]);
  return style;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an arrow inside a popover element.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const state = usePopoverState();
 * const props = usePopoverArrow({ state });
 * <Popover state={state}>
 *   <Role {...props} />
 *   Popover
 * </Popover>
 * ```
 */


var usePopoverArrow = createHook(function (_ref) {
  var _state, _state2, _state3;

  var state = _ref.state,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? defaultSize : _ref$size,
      props = _objectWithoutProperties(_ref, _excluded);

  var context = useContext(PopoverContext);
  state = state || context;
  var dir = (_state = state) == null ? void 0 : _state.currentPlacement.split("-")[0];
  var style = useComputedStyle((_state2 = state) == null ? void 0 : _state2.contentElement);
  var fill = (style == null ? void 0 : style.getPropertyValue("background-color")) || "none";
  var stroke = (style == null ? void 0 : style.getPropertyValue("border-" + dir + "-color")) || "none";
  var borderWidth = (style == null ? void 0 : style.getPropertyValue("border-" + dir + "-width")) || "0px";
  var strokeWidth = parseInt(borderWidth) * 2 * (defaultSize / size);
  var transform = rotateMap[dir];
  var children = useMemo(function () {
    return /*#__PURE__*/jsx("svg", {
      display: "block",
      viewBox: "0 0 30 30",
      children: /*#__PURE__*/jsxs("g", {
        transform: transform,
        children: [/*#__PURE__*/jsx("path", {
          fill: "none",
          d: POPOVER_ARROW_PATH
        }), /*#__PURE__*/jsx("path", {
          stroke: "none",
          d: POPOVER_ARROW_PATH
        })]
      })
    });
  }, [transform]);
  props = _objectSpread(_objectSpread({
    children: children,
    "aria-hidden": true
  }, props), {}, {
    ref: useForkRef((_state3 = state) == null ? void 0 : _state3.arrowRef, props.ref),
    style: _objectSpread({
      // server side rendering
      position: "absolute",
      fontSize: size,
      width: "1em",
      height: "1em",
      pointerEvents: "none",
      fill: fill,
      stroke: stroke,
      strokeWidth: strokeWidth
    }, props.style)
  });
  return props;
});
/**
 * A component that renders an arrow inside a `Popover` component.
 * @see https://ariakit.org/components/popover
 * @example
 * ```jsx
 * const popover = usePopoverState();
 * <Popover state={popover}>
 *   <PopoverArrow />
 *   Popover
 * </Popover>
 * ```
 */

var PopoverArrow = createComponent(function (props) {
  var htmlProps = usePopoverArrow(props);
  return createElement("div", htmlProps);
});
export { PopoverArrow, usePopoverArrow };